import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Battery, Moon, Utensils, Activity, AlertCircle, CheckCircle2, ArrowRight, Clock, Footprints, Coffee, XCircle, X } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Modal component for Privacy & Terms
function LegalModal({
  isOpen,
  onClose,
  type
}: {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms';
}) {
  const content = {
    privacy: {
      title: 'Política de Privacidad',
      content: <div className="space-y-4 text-gray-600 text-sm sm:text-base" data-id="element-0">
          <p data-id="element-1">
            <strong data-id="element-2">Última actualización:</strong> Enero 2024
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-3">
            1. Información que Recopilamos
          </h3>
          <p data-id="element-4">
            Recopilamos información personal que nos proporcionas
            voluntariamente al registrarte en nuestro programa, incluyendo
            nombre, correo electrónico y datos de pago.
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-5">
            2. Uso de la Información
          </h3>
          <p data-id="element-6">Utilizamos tu información para:</p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-7">
            <li data-id="element-8">Proporcionar acceso al programa Reset Hormonal de 21 Días</li>
            <li data-id="element-9">Enviar comunicaciones relacionadas con el programa</li>
            <li data-id="element-10">Mejorar nuestros servicios y contenido</li>
          </ul>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-11">
            3. Protección de Datos
          </h3>
          <p data-id="element-12">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            pérdida o alteración.
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-13">4. Tus Derechos</h3>
          <p data-id="element-14">
            Tienes derecho a acceder, rectificar, eliminar o limitar el uso de
            tus datos personales en cualquier momento.
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-15">5. Cookies</h3>
          <p data-id="element-16">
            Utilizamos cookies para mejorar tu experiencia en nuestro sitio.
            Puedes configurar tu navegador para rechazar cookies si lo
            prefieres.
          </p>
        </div>
    },
    terms: {
      title: 'Términos y Condiciones',
      content: <div className="space-y-4 text-gray-600 text-sm sm:text-base" data-id="element-17">
          <p data-id="element-18">
            <strong data-id="element-19">Última actualización:</strong> Enero 2024
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-20">
            1. Aceptación de los Términos
          </h3>
          <p data-id="element-21">
            Al acceder y utilizar el programa Reset Hormonal de 21 Días, aceptas
            estos términos y condiciones en su totalidad.
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-22">
            2. Descripción del Servicio
          </h3>
          <p data-id="element-23">
            El programa es un recurso educativo diseñado para proporcionar
            información sobre bienestar hormonal. No constituye consejo médico
            profesional.
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-24">
            3. Uso del Programa
          </h3>
          <ul className="list-disc pl-5 space-y-1" data-id="element-25">
            <li data-id="element-26">El acceso es personal e intransferible</li>
            <li data-id="element-27">
              No está permitido compartir, copiar o distribuir el contenido
            </li>
            <li data-id="element-28">El uso es bajo tu propia responsabilidad</li>
          </ul>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-29">
            4. Limitación de Responsabilidad
          </h3>
          <p data-id="element-30">
            Este programa es educativo y no sustituye el consejo médico
            profesional. Consulta siempre con un profesional de la salud antes
            de realizar cambios significativos en tu estilo de vida.
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-31">
            5. Política de Reembolso
          </h3>
          <p data-id="element-32">
            Ofrecemos una garantía de satisfacción de 7 días. Si no estás
            satisfecha con el programa, puedes solicitar un reembolso completo
            dentro de este período.
          </p>

          <h3 className="font-semibold text-gray-800 mt-6" data-id="element-33">
            6. Modificaciones
          </h3>
          <p data-id="element-34">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Los cambios entrarán en vigor inmediatamente después de su
            publicación.
          </p>
        </div>
    }
  };
  return <AnimatePresence data-id="element-35">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" data-id="element-36" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-2xl sm:w-full sm:max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50 flex flex-col" data-id="element-37">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100" data-id="element-38">
              <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C]" data-id="element-39">
                {content[type].title}
              </h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-40">
                <X className="w-5 h-5 text-gray-500" data-id="element-41" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6" data-id="element-42">
              {content[type].content}
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-100" data-id="element-43">
              <button onClick={onClose} className="w-full bg-[#6B7B5E] hover:bg-[#556B2F] text-white py-3 rounded-full font-medium transition-colors" data-id="element-44">
                Entendido
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function App() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#FDF8F3] text-gray-800" data-id="element-45">
      {/* Legal Modals */}
      <LegalModal isOpen={modalOpen === 'privacy'} onClose={() => setModalOpen(null)} type="privacy" data-id="element-46" />
      <LegalModal isOpen={modalOpen === 'terms'} onClose={() => setModalOpen(null)} type="terms" data-id="element-47" />

      {/* HERO SECTION */}
      <header className="relative pt-6 pb-10 sm:pt-10 sm:pb-16 lg:pt-24 lg:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-48">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center" data-id="element-49">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-4 sm:space-y-6 lg:space-y-8" data-id="element-50">
            <motion.div variants={fadeInUp} data-id="element-51">
              <span className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-[#6B7B5E]/10 text-[#6B7B5E] font-medium text-xs sm:text-sm tracking-wide mb-3 sm:mb-6" data-id="element-52">
                ESPECIAL PARA MUJERES 45+
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight text-[#2C2C2C] mb-2 sm:mb-4" data-id="element-53">
                Reset Hormonal <br data-id="element-54" />
                <span className="text-[#6B7B5E] italic" data-id="element-55">de 21 Días</span>
              </h1>
              <p className="text-base sm:text-xl lg:text-2xl text-gray-600 font-serif italic" data-id="element-56">
                El Método Semáforo para Recuperar Energía, Bajar Inflamación y
                Controlar Antojos
              </p>
            </motion.div>

            <motion.p variants={fadeInUp} className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed" data-id="element-57">
              Imagina abrir los ojos en la mañana y sentir, por primera vez en
              años, que tu energía está estable, que tu cuerpo no está
              inflamado, y que los antojos no te dominan al final del día.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3" data-id="element-58">
              <button onClick={() => {
              document.getElementById('cta-final')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="bg-[#6B7B5E] hover:bg-[#556B2F] text-white px-6 py-3 sm:px-8 sm:py-4 rounded-full text-base sm:text-lg font-medium transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-59">
                Comenzar Mi Reset Ahora
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-60" />
              </button>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative order-first lg:order-last" data-id="element-61">
            <div className="absolute inset-0 bg-[#6B7B5E]/5 rounded-2xl lg:rounded-[2rem] transform rotate-2 scale-105 -z-10" data-id="element-62" />
            <img src="https://cdn.magicpatterns.com/uploads/sAS5zs9AmMjiSbP83Jg7XH/heylloyd-image-1769821827241.png" alt="Mujer feliz y saludable usando el programa Reset Hormonal" className="w-full h-auto rounded-2xl lg:rounded-[2rem] shadow-xl lg:shadow-2xl" data-id="element-63" />
          </motion.div>
        </div>
      </header>

      {/* PAIN POINTS SECTION */}
      <section className="py-10 sm:py-14 lg:py-20 bg-white px-4 sm:px-6 lg:px-8" data-id="element-64">
        <div className="max-w-4xl mx-auto" data-id="element-65">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-66">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-3 sm:mb-6" data-id="element-67">
              La Frustración Silenciosa de la Mujer 45+
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-68">
              Si sientes que tu cuerpo ha cambiado y lo que antes funcionaba ya
              no da resultados, no estás sola. Y no es tu culpa.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-8" data-id="element-69">
            {[{
            icon: Utensils,
            text: "Haces 'todo bien' entre semana, pero los antojos te dominan el fin de semana."
          }, {
            icon: Activity,
            text: 'Intentas comer sano, pero terminas hinchada y con una barriga que no baja.'
          }, {
            icon: Moon,
            text: 'Te despiertas a las 3 a.m. sin poder dormir y sobrevives el día a base de café.'
          }, {
            icon: Battery,
            text: 'A pesar de tus esfuerzos, el peso sigue ahí, pegado como una sombra.'
          }].map((item, index) => <motion.div key={index} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={{
            hidden: {
              opacity: 0,
              y: 15
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                delay: index * 0.08
              }
            }
          }} className="flex items-start gap-3 p-4 sm:p-5 lg:p-6 rounded-xl lg:rounded-2xl bg-[#FDF8F3] border border-[#6B7B5E]/10" data-id="element-70">
                <div className="bg-[#6B7B5E]/10 p-2 sm:p-3 rounded-full shrink-0" data-id="element-71">
                  <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#6B7B5E]" data-id="element-72" />
                </div>
                <p className="text-sm sm:text-base text-gray-700 font-medium leading-relaxed" data-id="element-73">
                  {item.text}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHY FAILED SECTION */}
      <section className="py-10 sm:py-14 lg:py-20 bg-[#6B7B5E] text-white px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-74">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-75">
          <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-white rounded-full mix-blend-overlay blur-3xl transform translate-x-1/2 -translate-y-1/2" data-id="element-76"></div>
          <div className="absolute bottom-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-white rounded-full mix-blend-overlay blur-3xl transform -translate-x-1/2 translate-y-1/2" data-id="element-77"></div>
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-78">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-79">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 sm:mb-8 font-serif" data-id="element-80">
              Por Qué lo que Probaste Antes No Funcionó
            </h2>
            <div className="space-y-4 sm:space-y-6 text-sm sm:text-base lg:text-xl text-white/90 leading-relaxed" data-id="element-81">
              <p data-id="element-82">
                Las dietas tradicionales te dicen que comas menos, pero no te
                explican cómo estabilizar tu hambre y energía primero.
              </p>
              <p data-id="element-83">
                El ejercicio intenso te agota más, porque no toma en cuenta tu
                nivel hormonal actual. Y los suplementos aislados son solo
                parches temporales.
              </p>
              <p className="font-semibold text-white text-lg sm:text-xl lg:text-2xl mt-6 sm:mt-8 border-t border-white/20 pt-6 sm:pt-8" data-id="element-84">
                "Te han dado soluciones fragmentadas para un sistema complejo."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SEMAFORO METHOD SECTION */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-85">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-86">
          <span className="text-[#6B7B5E] font-semibold tracking-wider uppercase text-xs sm:text-sm" data-id="element-87">
            El Nuevo Enfoque
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#2C2C2C] mt-2 sm:mt-3 mb-3 sm:mb-6" data-id="element-88">
            El Semáforo Hormonal
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-89">
            Tu cuerpo envía señales. Este sistema te enseña a identificarlas y
            actuar antes de que se conviertan en crisis.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 lg:gap-8" data-id="element-90">
          {/* Red Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-2xl lg:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg lg:shadow-xl border-t-4 lg:border-t-8 border-red-600" data-id="element-91">
            <div className="bg-red-50 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center mb-4 sm:mb-6" data-id="element-92">
              <XCircle className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-red-600" data-id="element-93" />
            </div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-4" data-id="element-94">
              Modo Rojo
            </h3>
            <p className="text-gray-600 mb-3 sm:mb-4 font-medium text-sm sm:text-base" data-id="element-95">
              Alarma Total
            </p>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600" data-id="element-96">
              <li className="flex items-center gap-2" data-id="element-97">
                <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0" data-id="element-98"></span>
                Antojos fuertes
              </li>
              <li className="flex items-center gap-2" data-id="element-99">
                <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0" data-id="element-100"></span>
                Inflamación visible
              </li>
              <li className="flex items-center gap-2" data-id="element-101">
                <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0" data-id="element-102"></span>
                Despertares nocturnos
              </li>
            </ul>
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100 text-xs sm:text-sm font-medium text-red-700" data-id="element-103">
              Acción: Priorizar desayuno y rutina nocturna.
            </div>
          </motion.div>

          {/* Yellow Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={{
          ...fadeInUp,
          visible: {
            ...fadeInUp.visible,
            transition: {
              delay: 0.15
            }
          }
        }} className="bg-white rounded-2xl lg:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg lg:shadow-xl border-t-4 lg:border-t-8 border-amber-500" data-id="element-104">
            <div className="bg-amber-50 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center mb-4 sm:mb-6" data-id="element-105">
              <AlertCircle className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-amber-500" data-id="element-106" />
            </div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-4" data-id="element-107">
              Modo Amarillo
            </h3>
            <p className="text-gray-600 mb-3 sm:mb-4 font-medium text-sm sm:text-base" data-id="element-108">
              Precaución
            </p>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600" data-id="element-109">
              <li className="flex items-center gap-2" data-id="element-110">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-111"></span>
                Hambre ansiosa en la tarde
              </li>
              <li className="flex items-center gap-2" data-id="element-112">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-113"></span>
                Sueño ligero
              </li>
              <li className="flex items-center gap-2" data-id="element-114">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-115"></span>
                Energía fluctuante
              </li>
            </ul>
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100 text-xs sm:text-sm font-medium text-amber-700" data-id="element-116">
              Acción: Ajustar con caminata postcomida.
            </div>
          </motion.div>

          {/* Green Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={{
          ...fadeInUp,
          visible: {
            ...fadeInUp.visible,
            transition: {
              delay: 0.3
            }
          }
        }} className="bg-white rounded-2xl lg:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg lg:shadow-xl border-t-4 lg:border-t-8 border-green-600" data-id="element-117">
            <div className="bg-green-50 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center mb-4 sm:mb-6" data-id="element-118">
              <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-green-600" data-id="element-119" />
            </div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-4" data-id="element-120">
              Modo Verde
            </h3>
            <p className="text-gray-600 mb-3 sm:mb-4 font-medium text-sm sm:text-base" data-id="element-121">
              Regulación
            </p>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600" data-id="element-122">
              <li className="flex items-center gap-2" data-id="element-123">
                <span className="w-1.5 h-1.5 rounded-full bg-green-600 shrink-0" data-id="element-124"></span>
                Energía estable
              </li>
              <li className="flex items-center gap-2" data-id="element-125">
                <span className="w-1.5 h-1.5 rounded-full bg-green-600 shrink-0" data-id="element-126"></span>
                Sueño profundo
              </li>
              <li className="flex items-center gap-2" data-id="element-127">
                <span className="w-1.5 h-1.5 rounded-full bg-green-600 shrink-0" data-id="element-128"></span>
                Sin antojos
              </li>
            </ul>
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100 text-xs sm:text-sm font-medium text-green-700" data-id="element-129">
              Acción: Mantener las 3 palancas activas.
            </div>
          </motion.div>
        </div>
      </section>

      {/* THE 3 LEVERS SECTION */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white px-4 sm:px-6 lg:px-8" data-id="element-130">
        <div className="max-w-7xl mx-auto" data-id="element-131">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-132">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#2C2C2C] mb-3 sm:mb-6" data-id="element-133">
              Las 3 Palancas Diarias
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-134">
              Simples, efectivas y diseñadas para tu biología actual.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 lg:gap-12" data-id="element-135">
            {[{
            icon: Coffee,
            title: '1. Desayuno Estabilizador',
            desc: 'Proteína, fibra y grasa saludable para cortar los picos de hambre tempranos.'
          }, {
            icon: Footprints,
            title: '2. Caminata Postcomida',
            desc: '10-15 minutos de movimiento suave para bajar la inflamación.'
          }, {
            icon: Moon,
            title: '3. Rutina Nocturna',
            desc: '10 minutos para apagar el cortisol y preparar un descanso reparador.'
          }].map((lever, idx) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={{
            hidden: {
              opacity: 0,
              y: 20
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                delay: idx * 0.15
              }
            }
          }} className="flex flex-col items-center text-center" data-id="element-136">
                <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl lg:rounded-2xl bg-[#FDF8F3] flex items-center justify-center mb-4 sm:mb-6 text-[#6B7B5E] shadow-sm" data-id="element-137">
                  <lever.icon className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10" data-id="element-138" />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#2C2C2C] mb-2 sm:mb-4" data-id="element-139">
                  {lever.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed" data-id="element-140">
                  {lever.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section id="cta-final" className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#FDF8F3]" data-id="element-141">
        <div className="max-w-4xl mx-auto text-center" data-id="element-142">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-2xl lg:rounded-[2.5rem] p-6 sm:p-10 lg:p-16 shadow-xl lg:shadow-2xl border border-[#6B7B5E]/10" data-id="element-143">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#2C2C2C] mb-4 sm:mb-6" data-id="element-144">
              Tu Cuerpo Está Listo para el Cambio
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-10 leading-relaxed" data-id="element-145">
              En solo 21 días, recupera energía, baja inflamación y controla
              antojos.
              <br className="hidden sm:block" data-id="element-146" />
              <span className="font-medium text-[#6B7B5E]" data-id="element-147">
                Sin dietas extremas ni entrenamientos agotadores.
              </span>
            </p>

            <button onClick={() => window.open('https://equilibriovital360.com/cart/49902749253878:1', '_blank')} className="w-full sm:w-auto bg-[#6B7B5E] hover:bg-[#556B2F] text-white px-6 sm:px-10 py-4 sm:py-5 rounded-full text-base sm:text-lg lg:text-xl font-medium transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2 sm:gap-3 mx-auto" data-id="element-148">
              Quiero Comenzar Mi Reset
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-149" />
            </button>

            <div className="mt-4 flex items-center justify-center gap-3" data-id="element-150">
              <span className="text-gray-400 line-through text-base sm:text-lg" data-id="element-151">
                $19,99
              </span>
              <span className="text-2xl sm:text-3xl font-bold text-[#6B7B5E]" data-id="element-152">
                $6,39
              </span>
              <span className="bg-red-100 text-red-600 text-xs sm:text-sm font-semibold px-2 py-1 rounded-full" data-id="element-153">
                -68%
              </span>
            </div>

            <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500 flex items-center justify-center gap-2" data-id="element-154">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-155" />
              Acceso inmediato al programa completo
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#2C2C2C] text-white/60 py-6 sm:py-8 lg:py-12 text-center text-xs sm:text-sm" data-id="element-156">
        <div className="max-w-7xl mx-auto px-4" data-id="element-157">
          <div className="flex items-center justify-center gap-4 sm:gap-6 mb-4" data-id="element-158">
            <button onClick={() => setModalOpen('privacy')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-159">
              Política de Privacidad
            </button>
            <span className="text-white/30" data-id="element-160">|</span>
            <button onClick={() => setModalOpen('terms')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-161">
              Términos y Condiciones
            </button>
          </div>
          <p data-id="element-162">&copy; 2024 Reset Hormonal. Todos los derechos reservados.</p>
          <p className="mt-1 sm:mt-2" data-id="element-163">
            Este programa es educativo y no sustituye consejo médico
            profesional.
          </p>
        </div>
      </footer>
    </div>;
}